import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, ChevronLeft, Clock, Info, Key, Loader2, Search, Target } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

// ============ AI PRECEDENT FINDER ============

const PREC_CASE_TYPES = [
  { value: "personal_injury", label: "Personal Injury" },
  { value: "insurance_bad_faith", label: "Insurance Bad Faith" },
  { value: "employment", label: "Employment" },
  { value: "contract", label: "Contract" },
  { value: "medical_malpractice", label: "Medical Malpractice" },
  { value: "product_liability", label: "Product Liability" },
  { value: "fraud", label: "Fraud" },
  { value: "business_tort", label: "Business Tort" }
];

const PREC_JURISDICTIONS = [
  { value: "any", label: "Any Jurisdiction" },
  { value: "federal", label: "Federal Courts" },
  { value: "california", label: "California" },
  { value: "new_york", label: "New York" },
  { value: "texas", label: "Texas" },
  { value: "florida", label: "Florida" },
  { value: "illinois", label: "Illinois" }
];

const PrecedentFinderPage = () => {
  const [form, setForm] = useState({
    case_type: "personal_injury",
    jurisdiction: "any",
    key_facts: "",
    legal_issues: "",
    desired_outcome: "",
    date_range_years: 10
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  const search = async () => {
    if (!form.key_facts.trim()) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        case_type: form.case_type,
        jurisdiction: form.jurisdiction === 'any' ? '' : form.jurisdiction,
        key_facts: form.key_facts,
        legal_issues: form.legal_issues,
        desired_outcome: form.desired_outcome,
        date_range_years: form.date_range_years.toString()
      });
      const res = await axios.post(`${API}/precedent-finder/search?${params}`);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/precedent-finder/history`);
      setHistory(res.data.searches || []);
    } catch (e) {
      console.error(e);
    }
  };
  
  useEffect(() => {
    loadHistory();
  }, []);
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="precedent-finder-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-6xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>AI Precedent Finder</h1>
            <Badge className="bg-gradient-to-r from-teal-600 to-cyan-600">AI POWERED</Badge>
          </div>
          <p className="text-muted-foreground">Find and analyze relevant case precedents using AI</p>
        </div>
        
        {/* Search Form */}
        <Card className="mb-6">
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label>Case Type</Label>
                <Select value={form.case_type} onValueChange={v => setForm({...form, case_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PREC_CASE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Jurisdiction</Label>
                <Select value={form.jurisdiction} onValueChange={v => setForm({...form, jurisdiction: v})}>
                  <SelectTrigger><SelectValue placeholder="Any Jurisdiction" /></SelectTrigger>
                  <SelectContent>
                    {PREC_JURISDICTIONS.map(j => <SelectItem key={j.value || 'any'} value={j.value}>{j.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Date Range (years)</Label>
                <Select value={form.date_range_years.toString()} onValueChange={v => setForm({...form, date_range_years: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">Last 5 years</SelectItem>
                    <SelectItem value="10">Last 10 years</SelectItem>
                    <SelectItem value="20">Last 20 years</SelectItem>
                    <SelectItem value="50">Any time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <Label>Key Facts of Your Case *</Label>
              <Textarea 
                value={form.key_facts}
                onChange={e => setForm({...form, key_facts: e.target.value})}
                placeholder="Describe the key facts of your case that you want to find precedents for..."
                rows={4}
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Legal Issues (comma separated)</Label>
                <Input 
                  value={form.legal_issues}
                  onChange={e => setForm({...form, legal_issues: e.target.value})}
                  placeholder="e.g., negligence, proximate cause, duty of care"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Desired Outcome</Label>
                <Input 
                  value={form.desired_outcome}
                  onChange={e => setForm({...form, desired_outcome: e.target.value})}
                  placeholder="e.g., summary judgment for plaintiff"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={search} disabled={loading || !form.key_facts.trim()} data-testid="search-precedents-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
                Find Precedents
              </Button>
              <Button variant="outline" onClick={() => setShowHistory(!showHistory)}>
                <Clock className="h-4 w-4 mr-2" /> {showHistory ? 'Hide' : 'View'} History
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* History Panel */}
        {showHistory && history.length > 0 && (
          <Card className="mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Search History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {history.slice(0, 5).map((h, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer" onClick={() => setResult(h)}>
                    <div>
                      <p className="font-medium">{h.request?.case_type?.replace(/_/g, ' ')}</p>
                      <p className="text-sm text-muted-foreground">{h.request?.key_facts?.substring(0, 80)}...</p>
                    </div>
                    <Badge variant="outline">{h.total_relevant_cases} cases</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Results */}
        {result && (
          <div className="space-y-6 animate-fade-in" data-testid="precedent-results">
            {/* Summary Card */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>Precedent Analysis Results</CardTitle>
                    <CardDescription>{result.search_summary}</CardDescription>
                  </div>
                  <Badge className="bg-teal-600">{result.total_relevant_cases} Precedents Found</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {result.strongest_precedents?.length > 0 && (
                    <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                      <h4 className="font-semibold text-emerald-800 mb-2 flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4" /> Strongest Precedents
                      </h4>
                      <ul className="text-sm space-y-1">
                        {result.strongest_precedents.map((p, i) => <li key={i}>• {p}</li>)}
                      </ul>
                    </div>
                  )}
                  {result.legal_strategy_recommendations?.length > 0 && (
                    <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                        <Target className="h-4 w-4" /> Strategy Recommendations
                      </h4>
                      <ul className="text-sm space-y-1">
                        {result.legal_strategy_recommendations.map((r, i) => <li key={i}>• {r}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
            
            {/* Adverse Precedents Warning */}
            {result.potential_adverse_precedents?.length > 0 && (
              <Alert className="bg-amber-50 border-amber-200">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertDescription>
                  <span className="font-semibold text-amber-800">Potential Adverse Precedents:</span>
                  <ul className="mt-2 space-y-2">
                    {result.potential_adverse_precedents.map((ap, i) => (
                      <li key={i} className="text-sm">
                        <strong>{ap.case_name}</strong>: {ap.risk}
                        <span className="text-muted-foreground ml-2">Mitigation: {ap.mitigation}</span>
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            
            {/* Precedent Cards */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Relevant Precedents</h3>
              {result.precedents?.map((prec, i) => (
                <Card key={i} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold text-lg">{prec.case_name}</h4>
                        <p className="text-sm text-muted-foreground">{prec.citation} • {prec.court} • {prec.year}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={prec.outcome === 'plaintiff' ? 'default' : prec.outcome === 'defendant' ? 'secondary' : 'outline'}>
                          {prec.outcome?.charAt(0).toUpperCase() + prec.outcome?.slice(1)}
                        </Badge>
                        <Badge variant="outline" className="bg-teal-50">{Math.round(prec.relevance_score * 100)}% Match</Badge>
                      </div>
                    </div>
                    
                    <p className="text-sm mb-3">{prec.summary}</p>
                    
                    {prec.key_holdings?.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">Key Holdings</p>
                        <ul className="text-sm space-y-1">
                          {prec.key_holdings.map((h, j) => <li key={j} className="flex items-start gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-500 mt-1 shrink-0" />{h}</li>)}
                        </ul>
                      </div>
                    )}
                    
                    {prec.how_to_use && (
                      <div className="p-3 rounded-lg bg-slate-50 text-sm">
                        <span className="font-medium">How to Use: </span>{prec.how_to_use}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Research Gaps */}
            {result.research_gaps?.length > 0 && (
              <Card>
                <CardContent className="p-5">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Info className="h-4 w-4 text-blue-500" /> Research Gaps to Address
                  </h4>
                  <ul className="text-sm space-y-1">
                    {result.research_gaps.map((g, i) => <li key={i}>• {g}</li>)}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default PrecedentFinderPage;
